// 1. Imports
import { 
    Card, 
    Grid, 
    Tab, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TablePagination, 
    TableRow, 
    Tabs, 
    TextField, 
    Tooltip, 
    Typography 
} from "@mui/material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { MESSAGE_HISTORY_SENT_COLUMNS, MESSAGE_HISTORY_UNSENT_COLUMNS } from "../../../builders/message-history.builders";
import Loading from "../../loading";
import NoData from "../../no-data";
import { MessageCenterTypes } from "../../../types/message-center.types";
import { MessageHistoryFilters } from "./message-history-filters";
import { MessageDetailsModal } from "../../message-center/v2/message-history/message-history-details-modal";
import { MessageCenterService } from "../../../services/message-center.service";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { PaginatorFactory } from "../../../factory";
import { GT_COLORS } from "../../../constants/designTokens.constants";

// 2. Component Def
export const MessageHistoryList = () => {
    const [selectedMessage, setSelectedMessage] = useState<MessageCenterTypes.MessageBatch | null>(null);
    const [detailsModal, setDetailsModal] = useState<boolean>(false);
    const [messageRecords, setMessageRecords] = useState<MessageCenterTypes.MessageRecord[]>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    const blankDaysInThePastValue = 0;
    const defaultDaysInThePastValue = 30;
    const [daysInThePast, setDaysInThePast] = useState<number>(defaultDaysInThePastValue);
    const [showUpcomingBatches, setShowUpcomingBatches] = useState<string>('false');

    const {
        pagination: { total, items },
        loading,
    } = useSelector((state: RootState) => state.messageCenter);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });

    const onFilter = async (filters: { title: string; body: string }) => {
        await MessageCenterService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: { ...filters },
        },
        (isValid, msg) => {},
        false,
        showUpcomingBatches === 'true' ? true : false,
        isNaN(daysInThePast) ? blankDaysInThePastValue : daysInThePast);
    };

    const handleRowClick = async (message: MessageCenterTypes.MessageBatch) => {
        setSelectedMessage(message);
        setLoadingRecords(true);
        
        await MessageCenterService.getMessageRecordsByBatchId(
            message.id,
            (isValid, errorMessage, result) => {
                if (isValid && result) {
                    setMessageRecords(result);
                    setDetailsModal(true);
                } else {
                    console.error('Failed to fetch message records:', errorMessage);
                }
                setLoadingRecords(false);
            }
        );
    };

    useEffect(() => {
        MessageCenterService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: {},
        },
        (isValid, msg) => {},
        false,
        showUpcomingBatches === 'true' ? true : false,
        isNaN(daysInThePast) ? blankDaysInThePastValue : daysInThePast);
    }, [pagination?.perPage, pagination?.page, showUpcomingBatches, daysInThePast]);

    const columns = showUpcomingBatches === 'true' ? MESSAGE_HISTORY_UNSENT_COLUMNS: MESSAGE_HISTORY_SENT_COLUMNS;

    return (
        <>
            <Grid container display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ mb: 2 }}>
                <Tabs value={showUpcomingBatches} onChange={(e, v) => {
                    setShowUpcomingBatches(v);
                    setDaysInThePast(defaultDaysInThePastValue);
                }}>
                    <Tab label={"Sent Messages"} value={'false'} title="View sent messages"/>
                    <Tab label="Unsent/Scheduled" value={'true'} title="View unsent and scheduled messages"/>
                </Tabs>
                {showUpcomingBatches === 'false' && (
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            Displaying the last
                        </Typography>
                        <Tooltip title={"Days in the past"} placement="top">
                        <TextField
                            type="number"
                            value={daysInThePast}
                            onChange={(e) => {
                                // Only set the value if its positive, and round it to whole
                                const newValue = Math.max(0, parseInt(e.target.value, 10));
                                setDaysInThePast(newValue);
                            }}
                            inputProps={{ min: 0 }}
                            sx={{ width: 60 }}
                            onBlur={() => {
                                if (isNaN(daysInThePast)) {
                                    setDaysInThePast(blankDaysInThePastValue);
                                }
                            }}
                            onWheel={event => {
                                event.preventDefault();
                            }}
                        />
                        </Tooltip>
                        <Typography variant="body2" sx={{ mr: 1, ml: 1 }}>
                            days
                        </Typography>
                    </Grid>
                )}
            </Grid>
            
            <Card elevation={0} sx={{ mt: 2, border: `1px solid ${GT_COLORS.border}`, borderRadius: 2.5 }}>
                <MessageHistoryFilters
                    onFilter={onFilter}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: GT_COLORS.headerBg }}>
                                {columns?.map((column) => (
                                    <TableCell key={column.field}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                color: GT_COLORS.textMuted,
                                            }}
                                        >
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!loading.fetchMany && (items || [])?.map((item) => {
                                return (
                                    <TableRow
                                        sx={{
                                            ":hover": {
                                                backgroundColor: GT_COLORS.hoverBg,
                                                cursor: 'pointer'
                                            },
                                            '& td': { borderBottom: `1px solid ${GT_COLORS.borderLight}` },
                                        }} 
                                        key={`message_${item.id}`}
                                        onClick={() => {
                                            // Only fetch records for sent messages
                                            if (showUpcomingBatches === 'false' && item?.sent) {
                                                handleRowClick(item);
                                            } else {
                                                console.log('Cannot view details for unsent messages');
                                            }
                                        }}
                                    >
                                        {showUpcomingBatches === 'false' ? (
                                            <>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.deliveryDate ? dayjs(item?.deliveryDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.messageType || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.title || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.recipientUserIds?.length || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.channels?.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(', ') || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.deliveryDate ? dayjs(item?.deliveryDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.messageType || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.title || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.recipientUserIds?.length || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}
                                                    >
                                                        {item?.channels?.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(', ') || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>

                    <When 
                        condition={
                            loading.fetchMany === false && items?.length < 1
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={showUpcomingBatches === 'false' ? 'No sent messages' : 'No scheduled messages'}
                            description={
                                showUpcomingBatches === 'false'
                                    ? 'No messages have been sent in the selected time period.'
                                    : 'There are no unsent or scheduled messages.'
                            }
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component={"div"}
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) =>
                        setPagination((pag) => ({ ...pag, page: p }))
                    }
                    onRowsPerPageChange={(e) => 
                        setPagination((pag) => ({
                            ...pag,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>

            {/* Message Details Modal */}
            <MessageDetailsModal
                visible={detailsModal}
                onClose={() => {
                    setDetailsModal(false);
                    setSelectedMessage(null);
                    setMessageRecords([]);
                }}
                message={selectedMessage}
                records={messageRecords}
                loading={loadingRecords}
            />
        </>
    );
};